import { useState, useEffect } from 'react';
import { ref, onValue, query, orderByChild, Unsubscribe } from 'firebase/database';
import { database } from '@config/firebase';
import { useAuth } from '@context/AuthContext';
import { ensureChatMembership, sendTextMessage } from '../services/secureOperations';

export type ChatMessageType =
  | 'text'
  | 'property-card'
  | 'system'
  | 'mutual_like'
  | 'property_like'
  | 'property_liked_notification'
  | 'inquiry_request'
  | 'inquiry_sent';

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName?: string;
  senderType: 'tenant' | 'landlord' | 'system';
  text: string;
  type: ChatMessageType;
  timestamp: number;
  read: boolean;
  propertyId?: string;
  propertyTitle?: string;
  inquiryMessage?: string;
}

export const useChat = (roomId: string | undefined) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!roomId || !currentUser) {
      setLoading(false);
      return;
    }

    let unsubscribe: Unsubscribe | undefined;
    let cancelled = false;
    setLoading(true);

    const subscribe = async () => {
      try {
        await ensureChatMembership(roomId);
        if (cancelled) return;

        const messagesRef = ref(database, `chatMessages/${roomId}`);
        const messagesQuery = query(messagesRef, orderByChild('timestamp'));
        unsubscribe = onValue(messagesQuery, (snapshot) => {
          const data = snapshot.val() as Record<string, Omit<ChatMessage, 'id'>> | null;
          const messageList = data
            ? Object.entries(data)
              .filter(([, message]) => typeof message?.type === 'string')
              .map(([id, message]) => ({ id, ...message }))
              .sort((a, b) => a.timestamp - b.timestamp)
            : [];
          setMessages(messageList);
          setLoading(false);
        }, (error) => {
          console.error('Error loading chat messages:', error);
          setLoading(false);
        });
      } catch (error) {
        console.error('Error authorizing chat access:', error);
        if (!cancelled) setLoading(false);
      }
    };

    void subscribe();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [roomId, currentUser]);

  const sendMessage = async (text: string, matchId: string, _recipientId: string) => {
    if (!currentUser || !roomId || !text.trim()) return;

    try {
      await sendTextMessage(matchId, text.trim());
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    }
  };

  return { messages, sendMessage, loading };
};
